import { StyleSheet } from 'react-native';

import { webTapFloor, type UiTheme } from '../theme';

// Styles for the unified Add-Exercise picker (Esercizi / Superset tabs,
// multi-select, ⭐ favourites, superset panel). Extracted from ExercisePicker
// so the orchestrator stays under the 500-line cap. ≥44pt tap targets per
// [[feedback_user_visual_accessibility]].
export function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: t } = theme;
  return StyleSheet.create({
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: s.s5,
      paddingVertical: s.s4,
    },
    cancel: { color: p.ink2, fontSize: t.sizes.base },
    // A bare label is a ~20pt touch box, under the 44pt floor. The Pressable
    // carries the hitSlop; this keeps the row's own height honest so the
    // expansion has somewhere to grow into.
    cancelHit: { minHeight: 44, justifyContent: 'center' as const },
    // ⚠️ `flex: 1` + centred text, not a bare label leaning on
    // `justifyContent: space-between`. That arrangement only ever LOOKED
    // centred while both end cells were near-equal fixed boxes; since D2b the
    // right cell holds the place chip, whose width follows the place's name.
    // With flex the title owns the residual space and truncates inside it
    // instead of shoving the chip off the row.
    title: {
      flex: 1,
      textAlign: 'center',
      marginHorizontal: s.s3,
      color: p.ink,
      fontSize: t.sizes.base,
      fontWeight: '600',
    },
    // ⭐ The centred title works for EVERY mount again since DS-42 moved the
    // place chip out of the header into its own named band. The `titleLeading`
    // escape hatch that lived here existed only because a chip in the right
    // cell followed a place NAME and squeezed the residual box to ~13-15
    // characters on a 360dp phone, which cost "Заменить упражнение" and "Übung
    // tauschen" the most. Both end cells are fixed boxes again, so the reason
    // is gone and the style went with it instead of lingering unused.
    // The 64/56 divergence with the swap picker is the fake-centering the
    // planned <ScreenHeader> wave ends for good — not re-litigated here.
    headerSpacer: { width: 64 },

    // Tab bar — Esercizi | Superset.
    tabBar: {
      flexDirection: 'row',
      gap: s.s2,
      paddingHorizontal: s.s5,
      paddingBottom: s.s3,
    },
    tab: {
      flex: 1,
      alignItems: 'center',
      paddingVertical: s.s3,
      // PE-40 — horizontal breathing room, and it is not decoration. At THREE
      // tabs each cell is a third of the bar, and the visual bench showed the
      // Russian label ("Упражнения") filling its pill edge to edge and running
      // past the border. With padding the text has to wrap instead of
      // overflowing, which is legible; overflowing is not.
      paddingHorizontal: s.s2,
      borderRadius: r.r3,
      borderWidth: 1,
      borderColor: p.border,
      backgroundColor: p.surface,
      // ⛔ 44 on EVERY platform, not `webTapFloor` (which is undefined off the
      // web and left these at 40). The a11y floor this file declares at its top
      // is not a web rule, and three tabs make the miss easier to hit.
      minHeight: 44,
      justifyContent: 'center',
    },
    tabActive: { backgroundColor: p.brand500, borderColor: p.brand500 },
    tabText: {
      color: p.ink2,
      fontSize: t.sizes.base,
      fontWeight: '700',
      textAlign: 'center',
      // ⛔ `alignSelf: stretch`, NOT `flexShrink`. The tab is a COLUMN, so
      // flexShrink acts on its height and left the label free to grow past the
      // pill sideways — the bench showed the Russian label doing exactly that
      // and my first fix changed nothing. Stretching the text box to the
      // pill's width is what makes a long label WRAP instead of overflow.
      alignSelf: 'stretch',
    },
    tabTextActive: { color: p.inkOnBrand },
    // #336: the four-tab bar scrolls (PickerTabBar). The scroller does not
    // grow into the list's height; the padding gives the tabs' vertical
    // hitSlop room inside the clip, the negative margin gives it back. Each
    // tab keeps its natural width, so no label is ever cut.
    tabScroller: { flexGrow: 0, marginTop: -s.s2 },
    tabBarScroll: { paddingTop: s.s2 },
    tabNatural: { flex: 0, paddingHorizontal: s.s4 },

    // Search box and the Filters chip share one pinned row: collapsing the
    // two chip rows behind that toggle is what lets a search result be on
    // screen the moment you type, keyboard up (LG-33).
    searchWrap: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s.s3,
      paddingHorizontal: s.s5,
      paddingBottom: s.s3,
    },
    search: {
      flex: 1,
      backgroundColor: p.surface,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r3,
      color: p.ink,
      fontSize: t.sizes.base,
      paddingHorizontal: s.s4,
      paddingVertical: s.s3,
      // Since LG-32 this is the one control that never scrolls away, so it
      // carries the touch floor on its own rather than inheriting it from
      // the row of chips that used to sit beside it.
      minHeight: 44,
    },
    clearFilters: {
      paddingHorizontal: s.s5,
      paddingVertical: s.s2,
      // #223: a text link with no box measured ~28pt tall; the floor is 44 on
      // every platform (same reasoning as the tabs above).
      minHeight: 44,
      justifyContent: 'center',
      alignSelf: 'flex-end',
    },
    clearFiltersPressed: { opacity: 0.7 },
    clearFiltersText: {
      color: p.ink2,
      fontSize: t.sizes.sm,
      fontWeight: '600',
    },

    // List row.
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: s.s5,
      paddingVertical: s.s4,
      gap: s.s3,
    },
    rowPressed: { backgroundColor: p.surface2 },
    rowSelected: { backgroundColor: p.surface2 },
    rowBody: { flex: 1 },
    rowNameLine: { flexDirection: 'row', alignItems: 'center', gap: 6 },
    rowName: {
      color: p.ink,
      fontSize: t.sizes.base,
      fontWeight: '500',
      flexShrink: 1,
    },
    rowSub: { color: p.ink3, fontSize: t.sizes.sm, marginTop: 2 },
    // LG-41 — the "already there" badge sits on its own line under the
    // muscle, not inline with the name: the name line already truncates at
    // one line, and a badge beside it would eat the exercise name, which is
    // the one thing the row exists to show.
    rowMark: { marginTop: s.s1 },

    // ⭐ favourite toggle — a tap target on the right of each row.
    star: {
      width: 36,
      height: 36,
      minWidth: webTapFloor,
      minHeight: webTapFloor,
      alignItems: 'center',
      justifyContent: 'center',
    },
    starText: { fontSize: t.sizes.lg },
    starOn: { color: p.ink2 }, // favourite = filled vs empty carries state, not brand (S3.4)
    starOff: { color: p.ink3 },

    // The multi-select indicator is primitives/SelectMark (#336).

    separator: { height: StyleSheet.hairlineWidth, marginLeft: s.s5 },

    // The list OWNS the space left between the pinned search and the
    // footer, and never sizes to its content: without this the superset
    // panel in the list header pushed it to zero height (LG-32).
    // flexGrow on the content container is what lets the empty/loading
    // state below still fill that space instead of hugging the header.
    list: { flex: 1 },
    listContent: { flexGrow: 1 },
    // PE-40 — the Workout tab's consequence line, above its list.
    // ⛔ Its OWN weight, not `rowSub`'s: an a11y review caught it reading at
    // exactly the size and colour of every row's "3 exercises" subtitle, so the
    // one sentence that says an import fills six weeks was camouflaged among
    // the metadata it sits above.
    listHint: {
      paddingHorizontal: s.s5,
      paddingVertical: s.s3,
      color: p.ink2,
      fontSize: t.sizes.base,
    },

    center: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: s.s5 },
    errorText: { color: p.danger, fontSize: t.sizes.sm },

    // Footer action bar (multi-select confirm).
    footer: {
      paddingHorizontal: s.s5,
      paddingTop: s.s3,
      paddingBottom: s.s2,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: p.border,
    },
    // #336 - «In which days» above «Add N», the program editor's only.
    footerDays: { marginBottom: s.s3 },
    nameInput: {
      backgroundColor: p.surface,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r3,
      color: p.ink,
      fontSize: t.sizes.base,
      paddingHorizontal: s.s4,
      paddingVertical: s.s3,
      marginBottom: s.s2,
    },
    footerBtn: {
      backgroundColor: p.brand500,
      borderRadius: r.r3,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 44,
    },
    footerBtnDisabled: { backgroundColor: p.surface2 },
    footerBtnText: { color: p.inkOnBrand, fontSize: t.sizes.base, fontWeight: '700' },
    footerBtnTextDisabled: { color: p.ink3 },

    // Pinned superset context, one line above the search: the label and the
    // anchor exercise side by side. The chip shrinks (and truncates) rather
    // than pushing the label off screen on a long exercise name.
    contextStrip: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s.s3,
      paddingHorizontal: s.s5,
      paddingBottom: s.s3,
    },
    contextChip: { flexShrink: 1 },

    // Superset panel (above the list on the Superset tab).
    panel: {
      paddingHorizontal: s.s5,
      paddingBottom: s.s3,
      gap: s.s2,
    },
    panelLabel: {
      color: p.ink3,
      fontSize: t.sizes.xs,
      fontWeight: '700',
      letterSpacing: 0.6,
      textTransform: 'uppercase',
    },
    // rowGap (8) > columnGap (4): the chips carry a vertical-only hitSlop of 4,
    // so an 8px row gap keeps the wrapped-row touch halos from overlapping while
    // the columns stay tight (Antenore-shrink + wrap-overlap fix 2026-07-02).
    chipRow: { flexDirection: 'row', flexWrap: 'wrap', columnGap: s.s2, rowGap: s.s3 },
    chip: {
      paddingHorizontal: s.s4,
      borderRadius: 999,
      borderWidth: 1,
      borderColor: p.border,
      backgroundColor: p.surface,
      minHeight: webTapFloor ?? 36,
      justifyContent: 'center',
    },
    chipPressed: { opacity: 0.7 },
    chipActive: { backgroundColor: p.brand500, borderColor: p.brand500 },
    // Static anchor (the current exercise on the Superset tab) — a brand TINT,
    // distinct from the brand-FILL toggles so it doesn't read as tappable.
    chipAnchor: { backgroundColor: p.brand100, borderColor: p.brand500 },
    chipText: { color: p.ink2, fontSize: t.sizes.sm, fontWeight: '600' },
    chipTextActive: { color: p.inkOnBrand },
    templateMeta: { color: p.ink3, fontSize: t.sizes.xs, fontWeight: '500' },
    panelDivider: {
      height: StyleSheet.hairlineWidth,
      backgroundColor: p.border,
      marginTop: s.s1,
    },
  });
}

export type PickerStyles = ReturnType<typeof makeStyles>;
